import {Trans} from "@lingui/react/macro";

import {Checkbox} from "@khanacademy/wonder-blocks-form";
import Link from "@khanacademy/wonder-blocks-link";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {toAbsoluteUrl} from "@khan/urls";

const GLOBAL_FREE_TEACHER_TOOLS_TERMS_URL_V2 = toAbsoluteUrl(
    "/about/docs/grassroots-khanmigo-teacher-tools-terms",
);

// Path the the Terms of Service page
const TOS_PATH = "/about/docs/khan-academy-terms-of-service";

// Path to the Privacy Policy page
const PRIVACY_POLICY_PATH = "/about/privacy-policy";

/**
 * Our standard "you agree to" text for when a user signs up.
 */
export function StandardSignupLegalBlurb(): React.ReactNode {
    return (
        <LabelSmall>
            <Trans>
                By signing up, you agree to the{" "}
                <Link href={TOS_PATH} target="_blank">
                    Khan Academy Terms of Service
                </Link>{" "}
                and{" "}
                <Link href={PRIVACY_POLICY_PATH} target="_blank">
                    Privacy Policy
                </Link>
                .
            </Trans>
        </LabelSmall>
    );
}

/**
 * Similar to our StandardSignupLegalBlurb, but for teachers. It includes an
 * additional link and agreement to the Khanmigo Teacher Tools supplement.
 */
export function TeacherSignupLegalBlurb(): React.ReactNode {
    const termsUrl = GLOBAL_FREE_TEACHER_TOOLS_TERMS_URL_V2;

    return (
        <LabelSmall>
            <Trans>
                By signing up, you agree to the{" "}
                <Link href={TOS_PATH} target="_blank">
                    Khan Academy Terms of Service
                </Link>
                ,{" "}
                <Link href={termsUrl} target="_blank">
                    Khanmigo Teacher Tools supplement
                </Link>
                , and{" "}
                <Link href={PRIVACY_POLICY_PATH} target="_blank">
                    Privacy Policy
                </Link>
                .
            </Trans>
        </LabelSmall>
    );
}

interface TransferAccountLegalBlurbProps {
    /**
     * Whether the checkbox is checked.
     */
    checked: boolean;

    /**
     * Function to set the checked state.
     */
    onChange: (checked: boolean) => void;
}

/**
 * Legal blurb with checkbox for transferring accounts
 */
export function TransferAccountLegalBlurb({
    checked,
    onChange,
}: TransferAccountLegalBlurbProps): React.ReactNode {
    return (
        <Checkbox
            checked={checked}
            onChange={onChange}
            label={
                <LabelSmall>
                    <Trans>
                        By transferring, you agree to the{" "}
                        <Link href={TOS_PATH} target="_blank">
                            Khan Academy Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link href={PRIVACY_POLICY_PATH} target="_blank">
                            Privacy Policy
                        </Link>
                        .
                    </Trans>
                </LabelSmall>
            }
        />
    );
}

export const SignUpLegalCheckboxBlurb = () => {
    return (
        <LabelSmall>
            <Trans>
                By checking this box, I agree to the{" "}
                <Link href={TOS_PATH} target="_blank">
                    Khan Academy Terms of Service
                </Link>{" "}
                and{" "}
                <Link href={PRIVACY_POLICY_PATH} target="_blank">
                    Privacy Policy
                </Link>
                .
            </Trans>
        </LabelSmall>
    );
};
